import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenantConfig, useCart, useCartActions } from '@webmono/storefront-ui/providers'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import { useCreateOrder } from '@/lib/hooks'
import { SchedulePicker } from '@/components/SchedulePicker'
import { AddressForm } from '@/components/AddressForm'
import { LoyaltyRedemption } from '@/components/LoyaltyRedemption'

const TIP_PRESETS = [15, 18, 20, 25]

const MOCK_CUSTOMER_LOYALTY = {
  currentPoints: 850,
  currentTier: 'silver',
}

export function CheckoutPage() {
  const tenant = useTenantConfig()
  const navigate = useNavigate()
  const { items, totals, fulfillmentType, tableNumber, tip, scheduledFor, deliveryAddress, loyaltyRedemption } = useCart()
  const { setFulfillmentType, setTip, setScheduledFor, setDeliveryAddress } = useCartActions()
  const createOrder = useCreateOrder()

  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customTip, setCustomTip] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isTableService = tableNumber !== undefined

  const handleTipSelect = (percent: number) => {
    const tipAmount = Math.round(totals.subtotalCents * (percent / 100))
    setTip({ type: 'preset', amountCents: tipAmount, percent })
    setCustomTip('')
  }

  const handleCustomTip = (value: string) => {
    setCustomTip(value)
    const amount = parseFloat(value) || 0
    setTip({ type: 'custom', amountCents: Math.round(amount * 100) })
  }

  const handleNoTip = () => {
    setTip({ type: 'none', amountCents: 0 })
    setCustomTip('')
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!customerName.trim()) newErrors.name = 'Name is required'
    if (!customerPhone.trim()) newErrors.phone = 'Phone is required'
    if (customerPhone.trim() && !/^\+?[\d\s-()]{10,}$/.test(customerPhone)) {
      newErrors.phone = 'Invalid phone number'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setIsSubmitting(true)

    try {
      const order = await createOrder.mutateAsync({
        customerName,
        customerPhone,
        customerEmail: customerEmail || undefined,
      })
      navigate(`/order/${order.id}`)
    } catch {
      setErrors({ submit: 'Failed to place order. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (items.length === 0) {
    navigate('/menu')
    return null
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-4 pb-32">
      <button
        onClick={() => navigate('/cart')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Cart
      </button>

      <h1 className="font-heading text-xl font-semibold text-foreground mb-6">Checkout</h1>

      {!isTableService && (
        <section className="mb-6">
          <h2 className="font-medium text-foreground mb-3">How would you like to get your order?</h2>
          <div className="grid grid-cols-2 gap-3">
            {tenant.features.pickup && (
              <button
                onClick={() => setFulfillmentType('pickup')}
                className={cn(
                  'p-4 rounded-lg border-2 text-center transition-all',
                  fulfillmentType === 'pickup'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <svg className="w-6 h-6 mx-auto mb-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <span className="font-medium text-foreground">Pickup</span>
                <p className="text-xs text-muted-foreground mt-1">
                  Ready in ~{tenant.fulfillment.pickup.leadTimeMinutes} min
                </p>
              </button>
            )}
            {tenant.features.delivery && (
              <button
                onClick={() => setFulfillmentType('delivery')}
                className={cn(
                  'p-4 rounded-lg border-2 text-center transition-all',
                  fulfillmentType === 'delivery'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                )}
              >
                <svg className="w-6 h-6 mx-auto mb-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                </svg>
                <span className="font-medium text-foreground">Delivery</span>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCents(tenant.fulfillment.delivery.flatFeeCents, tenant.operations.currency)} fee
                </p>
              </button>
            )}
          </div>
        </section>
      )}

      {isTableService && (
        <section className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <div>
              <h3 className="font-medium text-foreground">Table Service</h3>
              <p className="text-sm text-muted-foreground">Your order will be brought to Table {tableNumber}</p>
            </div>
          </div>
        </section>
      )}

      {!isTableService && (
        <section className="mb-6">
          <h2 className="font-medium text-foreground mb-3">When do you want it?</h2>
          <SchedulePicker
            value={scheduledFor}
            onChange={setScheduledFor}
            leadTimeMinutes={
              fulfillmentType === 'delivery'
                ? tenant.fulfillment.delivery.leadTimeMinutes
                : tenant.fulfillment.pickup.leadTimeMinutes
            }
          />
        </section>
      )}

      {fulfillmentType === 'delivery' && (
        <section className="mb-6">
          <h2 className="font-medium text-foreground mb-3">Delivery Address</h2>
          <AddressForm
            value={deliveryAddress}
            onChange={setDeliveryAddress}
          />
        </section>
      )}

      <section className="mb-6">
        <h2 className="font-medium text-foreground mb-3">Contact Information</h2>
        <div className="space-y-3">
          <div>
            <input
              type="text"
              placeholder="Your name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className={cn(
                'w-full px-4 py-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                errors.name ? 'border-destructive' : 'border-input'
              )}
            />
            {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
          </div>
          <div>
            <input
              type="tel"
              placeholder="Phone number"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className={cn(
                'w-full px-4 py-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                errors.phone ? 'border-destructive' : 'border-input'
              )}
            />
            {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
          </div>
          <input
            type="email"
            placeholder="Email (optional, for receipt)"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </section>

      {tenant.features.tipping && (
        <section className="mb-6">
          <h2 className="font-medium text-foreground mb-3">Add a tip?</h2>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {TIP_PRESETS.map((percent) => (
              <button
                key={percent}
                onClick={() => handleTipSelect(percent)}
                className={cn(
                  'py-2 rounded-lg border-2 text-center transition-all',
                  tip?.percent === percent
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/50 text-foreground'
                )}
              >
                <span className="font-medium">{percent}%</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                placeholder="Custom"
                value={customTip}
                onChange={(e) => handleCustomTip(e.target.value)}
                className="w-full pl-7 pr-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <button
              onClick={handleNoTip}
              className={cn(
                'px-4 py-2 rounded-lg border-2 transition-all',
                tip?.type === 'none'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/50 text-muted-foreground'
              )}
            >
              No tip
            </button>
          </div>
        </section>
      )}

      {tenant.features.loyalty && tenant.loyalty?.enabled && (
        <section className="mb-6">
          <LoyaltyRedemption
            currentPoints={MOCK_CUSTOMER_LOYALTY.currentPoints}
            currentTier={MOCK_CUSTOMER_LOYALTY.currentTier}
            selectedRedemption={loyaltyRedemption}
          />
        </section>
      )}

      {errors.submit && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-center text-sm">
          {errors.submit}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-card border-t border-border safe-bottom">
        <div className="max-w-lg mx-auto">
          {loyaltyRedemption && (
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-success">{loyaltyRedemption.optionName}</span>
              <span className="text-success">
                -{formatCents(loyaltyRedemption.discountCents, tenant.operations.currency)}
              </span>
            </div>
          )}
          <div className="flex justify-between mb-3">
            <span className="text-muted-foreground">Total</span>
            <span className="font-semibold text-foreground">
              {formatCents(totals.totalCents + (tip?.amountCents || 0), tenant.operations.currency)}
            </span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={cn(
              'w-full py-3 rounded-full font-medium text-center transition-all',
              isSubmitting
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]'
            )}
          >
            {isSubmitting ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      </div>
    </div>
  )
}
